import { readCookie } from '@zapier/cookies';
import { _ as _objectSpread2 } from './objectSpread2-cdde903d.esm.js';
import _, { pickBy, extend, omitBy, curryRight, capitalize } from 'lodash';
import querystring from 'querystring';
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { REBRAND_COOKIE, Colors, useTheme, Spinner, TooltipWrapper, Button, Icon, IconButton } from '@zapier/design-system';
import { _ as _objectWithoutProperties } from './ZapSharingProvider-9b241926.esm.js';
import { useTrackingContext, createEventTracker, TrackingProvider } from '@zapier/toolbox-tracking';
import { jsx, css } from '@emotion/react';

const splitUrlPiece = (splitter, url = '') => {
  if (url.indexOf(splitter) < 0) {
    url = `${url}${splitter}`;
  }

  const splitterIndex = url.indexOf(splitter);
  return {
    baseHref: url.substring(0, splitterIndex),
    queryString: url.substring(splitterIndex + 1)
  };
};

const splitUrl = _.partial(splitUrlPiece, '?');

const filterEmptyKeys = obj => pickBy(obj, (_, key) => !!key);

const extractParams = url => {
  url = url || '';
  const params = querystring.parse(splitUrl(url).queryString); // Handles a params object that was extracted from an URL with a trailing `&`,
  // which would result in an empty key/value pair.

  return filterEmptyKeys(params);
};

const makeQueryString = (oldParams, mergeParams) => {
  const newParams = extend({}, oldParams, mergeParams);
  return querystring.stringify(newParams);
};

const adjustUrlParams = (adjust, url) => {
  const parts = splitUrl(url);
  const newParams = adjust(extractParams(url)) || {};
  const newUrl = `${parts.baseHref}?${makeQueryString(newParams)}`; // Strip trailing `?` if there are no params

  return newUrl.replace(/\?$/, '');
};

const isEmptyString = string => !string || /^\s*$/.test(string);

const sanitize = (params, onlyTruthyParams) => onlyTruthyParams ? omitBy(params, isEmptyString) : params;

const addParamsToUrl = (url, params = {}, onlyTruthyParams = false) => adjustUrlParams(currentParams => _objectSpread2(_objectSpread2({}, currentParams), sanitize(params, onlyTruthyParams)), url);

const POPUP_SPLASH_URL = '/window-splash/';
const POPUP_WINDOW_OPTIONS = 'height=700,width=980,scrollbars=yes'; // Create the popup window and return the new window object ref.
// If the window cannot be created (is blocked) then null is returned.
// See https://developer.mozilla.org/en-US/docs/Web/API/Window/open#Return_value

const createPopupWindow = url => window.open(url, '_blank', POPUP_WINDOW_OPTIONS); // Decorate the original url with a /csrf/fix view/url that will set the missing csrf
// token. This is a useful workaround for Safari's security policy that block cookies
// in iframes if the user has never visited the site outside of an iframe.
//
// Note: The backend requires the `next` url to be strictly the POPUP_SPLASH_URL based
// on security concerns.


const maybeCsrfFix = url => !readCookie('csrftoken') ? addParamsToUrl('/csrf/fix/', {
  next: url
}) : url;

const createPopupSplash = () => new Promise((resolve, reject) => {
  const {
    origin
  } = window.location;
  const popup = createPopupWindow(maybeCsrfFix(POPUP_SPLASH_URL)); // When popups are blocked by the browser `popup` will be falsey
  // so we return early here.

  if (!popup) {
    reject(new Error('Popup was blocked.'));
    return;
  } // This code will setup a postMessage listener that will listen for an event
  // with the data payload of `ready`. Assuming that the origins match, this
  // will then trigger the resolve of the `popup` which will eventually be used
  // to redirect the popup to the desired authentication URL.
  // This code exists for cross-browser purposes, binding to the `popup`
  // `DOMContentLoaded` or `load` event will not work in all browsers.


  const onPostMessageAuthPopUpReady = event => {
    if (event.origin === origin && event.data === 'ready') {
      window.removeEventListener('message', onPostMessageAuthPopUpReady);
      resolve(popup);
    }
  };

  window.addEventListener('message', onPostMessageAuthPopUpReady, false);
});

const createSocialMediaWindow = async ({
  resource,
  shareLink
}) => {
  // Create the splash popup if possible, then retrieve the connect URL
  // to ensure it is valid. We do these as separate steps because we want the
  // popup window to open ASAP rather than waiting for the `fetchConnectUrl` to
  // resolve.
  const win = await createPopupSplash();
  const urls = {
    Facebook: 'https://www.facebook.com/sharer/sharer.php',
    Twitter: 'https://twitter.com/intent/tweet',
    LinkedIn: 'https://www.linkedin.com/shareArticle?mini=true'
  };
  const resourceParams = {
    Facebook: 'u',
    Twitter: 'text',
    LinkedIn: 'url'
  };
  const url = urls[resource];
  const param = resourceParams[resource];
  const parameterizedUrl = addParamsToUrl(url, {
    [param]: shareLink
  });

  try {
    win.location.href = parameterizedUrl;
  } catch (e) {
    throw new Error(e);
  }
};

const useIsRebrand = () => {
  const [isRebrand, setIsRebrand] = useState(false);
  useEffect(() => {
    const rebrandCookie = readCookie(REBRAND_COOKIE) === 'true';
    setIsRebrand(rebrandCookie);
  }, []);
  return isRebrand;
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const NamedTrackingContext = /*#__PURE__*/React.createContext(undefined);

function isZapSharingModalMetadata(metadata) {
  if (metadata === undefined) {
    return false;
  }

  const {
    link_hash,
    location_name
  } = metadata;
  return link_hash !== undefined && location_name !== undefined;
}
function isPostEnableModalMetadata(metadata) {
  if (metadata === undefined) {
    return false;
  }

  const {
    customuser_id,
    zap_id
  } = metadata;
  return customuser_id !== undefined && zap_id !== undefined;
}
function isTeamsAndCompaniesDashboardMetadata(metadata) {
  return metadata !== undefined && metadata.session_id !== undefined;
}
function isSharedZapLanderMetadata(metadata) {
  return metadata !== undefined && metadata.context_lander_type_name !== undefined;
}

const CHANNEL_EVENT_NAME = 'schema:zap.sharing.ZapShareChannelSendRequestEvent';
const DASHBOARD_EVENT_NAME = 'schema:event.zapier.sharing.dashboard.ui.SharingDashboardUIActionEvent';
const LANDER_EVENT_NAME = 'schema:zap.sharing.ZapShareLanderUserInteractionEvent';
const MODAL_EVENT_NAME = 'schema:zap.sharing.ZapShareModalUserInteractionEvent';
const POSTENABLE_EVENT_NAME = 'shared zap: post_enable_modal';

function init$3(metadata, trackEvent) {
  const defaults = {
    channel: null,
    sz_id: null
  };
  return {
    closed() {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'closed_modal'
      }));
    },

    optOutClicked() {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'click_opt_out'
      }));
    },

    settingsClicked(sharedZapId) {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'click_share_settings',
        sz_id: sharedZapId
      }));
    },

    shareLinkCopied(sharedZapId) {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'copy_link',
        sz_id: sharedZapId
      }));
    },

    shown() {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'viewed_modal'
      }));
    },

    socialChannelClicked(channel, sharedZapId) {
      return trackEvent(POSTENABLE_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        action: 'click_channel',
        channel,
        sz_id: sharedZapId
      }));
    }

  };
}

const createPostEnableModalTracker = curryRight(init$3);

function init$2(metadata, trackEvent) {
  let _metadata = Object.assign({}, metadata);

  const trackLanderEvent = options => {
    return trackEvent(LANDER_EVENT_NAME, _objectSpread2(_objectSpread2({}, _metadata), {}, {
      component_name: options.component_name,
      action_name: options.action_name,
      preconfigured_zap_state_name: options.preconfigured_zap_state_name == null ? null : options.preconfigured_zap_state_name,
      denied_invalid_selected_reasons: options.denied_invalid_selected_reasons == null ? null : options.denied_invalid_selected_reasons,
      reviewer_more_info: options.reviewer_more_info == null ? null : options.reviewer_more_info,
      video_duration_seconds: options.video_duration_seconds == null ? null : options.video_duration_seconds,
      video_watched_duration_seconds: options.video_watched_duration_seconds == null ? null : options.video_watched_duration_seconds
    }));
  };

  return {
    appClicked: trackLanderEvent,
    bannerEditClicked: trackLanderEvent,
    bannerShareClicked: trackLanderEvent,
    bannerSignUpClicked: trackLanderEvent,
    entered: trackLanderEvent,
    exploreClicked: trackLanderEvent,
    howItWorksClicked: trackLanderEvent,
    howItWorksCommentAdded: trackLanderEvent,
    howItWorksCommentDeleted: trackLanderEvent,
    howItWorksCommentEdited: trackLanderEvent,
    learnMoreClicked: trackLanderEvent,
    reviewerChangedStatus: trackLanderEvent,
    titleEditClicked: trackLanderEvent,
    tourInteraction: trackLanderEvent,
    tryThisZapClicked: trackLanderEvent,
    videoInteraction: trackLanderEvent,
    viewed: trackLanderEvent,
    zapTemplateClicked: trackLanderEvent,

    setMetadata(newMetadata) {
      _metadata = newMetadata;
    }

  };
}

const createSharedZapLanderTracker = curryRight(init$2);

function init$1(metadata, trackEvent) {
  const defaults = {
    shared_zap_id: null,
    zap_template_id: null,
    zap_id: null,
    field: null,
    value: null,
    index: null
  };
  return {
    entered(component_name) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name,
        event_action: 'enter'
      }));
    },

    carouselDirectionClicked({
      component_name,
      direction,
      index
    }) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name,
        event_action: 'click',
        value: direction,
        index
      }));
    },

    carouselDismissClicked({
      index,
      shared_zap_id
    }) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'shared_zap_inbox',
        event_action: 'dismiss',
        shared_zap_id,
        value: 'shared_zap_card',
        index
      }));
    },

    modalDismissed() {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'tutorial_modal',
        event_action: 'dismiss'
      }));
    },

    sharedZapCardClicked({
      component_name,
      index,
      shared_zap_id
    }) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name,
        event_action: 'click',
        shared_zap_id,
        value: 'shared_zap_card',
        index
      }));
    },

    viewed({
      component_name,
      event_action
    }) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name,
        event_action
      }));
    },

    zapCardClicked({
      component_name,
      index,
      zap_id
    }) {
      return trackEvent(DASHBOARD_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name,
        event_action: 'click',
        zap_id,
        value: 'shared_zap_card',
        index
      }));
    }

  };
}

const createTeamsAndCompaniesDashboardTracker = curryRight(init$1);

function init(metadata, trackEvent) {
  const defaults = {
    social_share_channel_name: null,
    permissions_level_name: null
  };
  return {
    shown() {
      return trackEvent(MODAL_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'modal',
        action_name: 'enter'
      }));
    },

    socialChannelShared(event) {
      return trackEvent(CHANNEL_EVENT_NAME, event);
    },

    shareLinkCopied() {
      return trackEvent(MODAL_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'link',
        action_name: 'copy'
      }));
    },

    socialChannelClicked(channel) {
      return trackEvent(MODAL_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'channel',
        action_name: 'click',
        social_share_channel_name: channel
      }));
    },

    sharingModeChanged(mode) {
      return trackEvent(MODAL_EVENT_NAME, _objectSpread2(_objectSpread2(_objectSpread2({}, defaults), metadata), {}, {
        component_name: 'sharing_permissions_menu_item',
        action_name: 'update',
        permissions_level_name: mode
      }));
    }

  };
}

const createZapSharingModalTracker = curryRight(init);

function NamedTrackingProvider(props) {
  const trackingFunctions = useTrackingContext();
  const {
    trackEvent
  } = trackingFunctions;
  const value = React.useMemo(() => _objectSpread2(_objectSpread2({}, trackingFunctions), {}, {
    postEnableModal: createPostEnableModalTracker(isPostEnableModalMetadata(props.metadata) ? props.metadata : undefined, trackEvent),
    sharedZapLander: createSharedZapLanderTracker(isSharedZapLanderMetadata(props.metadata) ? props.metadata : undefined, trackEvent),
    teamsAndCompaniesDashboard: createTeamsAndCompaniesDashboardTracker(isTeamsAndCompaniesDashboardMetadata(props.metadata) ? props.metadata : undefined, trackEvent),
    zapSharingModal: createZapSharingModalTracker(isZapSharingModalMetadata(props.metadata) ? props.metadata : undefined, trackEvent)
  }), [props.metadata, trackEvent, trackingFunctions]);
  return jsx(NamedTrackingContext.Provider, {
    value: value
  }, props.children);
}

function NamedTrackingProviderContainer(_ref) {
  let {
    trackingFunctions = createEventTracker()
  } = _ref,
      props = _objectWithoutProperties(_ref, ["trackingFunctions"]);

  return jsx(TrackingProvider, {
    trackingFunctions: trackingFunctions
  }, jsx(NamedTrackingProvider, props));
}

function useNamedTracking() {
  const context = React.useContext(NamedTrackingContext);

  if (!context) {
    throw new Error(`'useNamedTracking' must be used within a NamedTrackingProvider`);
  }

  return context;
}

function _EMOTION_STRINGIFIED_CSS_ERROR__$1() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
const spinner$1 = process.env.NODE_ENV === "production" ? {
  name: "b56azp",
  styles: "align-items:center;display:flex;height:40px;justify-content:center;width:40px"
} : {
  name: "1kep0ek-spinner",
  styles: "align-items:center;display:flex;height:40px;justify-content:center;width:40px;label:spinner;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFFMEIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vcmVhY3QnO1xuXG5leHBvcnQgY29uc3Qgc3Bpbm5lciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAgaGVpZ2h0OiA0MHB4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3Qgc2hhcmVJY29uID0gKGljb25Vcmw6IHN0cmluZykgPT4gY3NzYFxuICBiYWNrZ3JvdW5kLWltYWdlOiB1cmwoJHtpY29uVXJsfSk7XG4gIGJhY2tncm91bmQtcG9zaXRpb246IGNlbnRlcjtcbiAgYmFja2dyb3VuZC1yZXBlYXQ6IG5vLXJlcGVhdDtcbiAgYmFja2dyb3VuZC1zaXplOiBjb250YWluO1xuICBkaXNwbGF5OiBibG9jaztcbiAgaGVpZ2h0OiAyMnB4O1xuICB3aWR0aDogNDBweDtcbmA7XG5cbmV4cG9ydCBjb25zdCBpY29uV3JhcHBlciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcbiJdfQ== */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$1
};
const shareIcon$1 = iconUrl => /*#__PURE__*/css("background-image:url(", iconUrl, ");background-position:center;background-repeat:no-repeat;background-size:contain;display:block;height:22px;width:40px;" + (process.env.NODE_ENV === "production" ? "" : ";label:shareIcon;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFVaUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vcmVhY3QnO1xuXG5leHBvcnQgY29uc3Qgc3Bpbm5lciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAgaGVpZ2h0OiA0MHB4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3Qgc2hhcmVJY29uID0gKGljb25Vcmw6IHN0cmluZykgPT4gY3NzYFxuICBiYWNrZ3JvdW5kLWltYWdlOiB1cmwoJHtpY29uVXJsfSk7XG4gIGJhY2tncm91bmQtcG9zaXRpb246IGNlbnRlcjtcbiAgYmFja2dyb3VuZC1yZXBlYXQ6IG5vLXJlcGVhdDtcbiAgYmFja2dyb3VuZC1zaXplOiBjb250YWluO1xuICBkaXNwbGF5OiBibG9jaztcbiAgaGVpZ2h0OiAyMnB4O1xuICB3aWR0aDogNDBweDtcbmA7XG5cbmV4cG9ydCBjb25zdCBpY29uV3JhcHBlciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcbiJdfQ== */");
const iconWrapper = process.env.NODE_ENV === "production" ? {
  name: "r6mxso",
  styles: "align-items:center;display:flex;justify-content:center;width:40px"
} : {
  name: "ky6ky4-iconWrapper",
  styles: "align-items:center;display:flex;justify-content:center;width:40px;label:iconWrapper;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFvQjhCIiwiZmlsZSI6InN0eWxlcy50cyIsInNvdXJjZXNDb250ZW50IjpbImltcG9ydCB7IGNzcyB9IGZyb20gJ0BlbW90aW9uL3JlYWN0JztcblxuZXhwb3J0IGNvbnN0IHNwaW5uZXIgPSBjc3NgXG4gIGFsaWduLWl0ZW1zOiBjZW50ZXI7XG4gIGRpc3BsYXk6IGZsZXg7XG4gIGhlaWdodDogNDBweDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IHNoYXJlSWNvbiA9IChpY29uVXJsOiBzdHJpbmcpID0+IGNzc2BcbiAgYmFja2dyb3VuZC1pbWFnZTogdXJsKCR7aWNvblVybH0pO1xuICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XG4gIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XG4gIGJhY2tncm91bmQtc2l6ZTogY29udGFpbjtcbiAgZGlzcGxheTogYmxvY2s7XG4gIGhlaWdodDogMjJweDtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3QgaWNvbldyYXBwZXIgPSBjc3NgXG4gIGFsaWduLWl0ZW1zOiBjZW50ZXI7XG4gIGRpc3BsYXk6IGZsZXg7XG4gIGp1c3RpZnktY29udGVudDogY2VudGVyO1xuICB3aWR0aDogNDBweDtcbmA7XG4iXX0= */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$1
};

function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
const spinner = process.env.NODE_ENV === "production" ? {
  name: "b56azp",
  styles: "align-items:center;display:flex;height:40px;justify-content:center;width:40px"
} : {
  name: "1kep0ek-spinner",
  styles: "align-items:center;display:flex;height:40px;justify-content:center;width:40px;label:spinner;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInJlYnJhbmRTdHlsZXMudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBRzBCIiwiZmlsZSI6InJlYnJhbmRTdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9yZWFjdCc7XG5pbXBvcnQgeyBDb2xvcnMgfSBmcm9tICdAemFwaWVyL2Rlc2lnbi1zeXN0ZW0nO1xuXG5leHBvcnQgY29uc3Qgc3Bpbm5lciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAgaGVpZ2h0OiA0MHB4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3Qgc2hhcmVJY29uID0gKGljb25Vcmw6IHN0cmluZykgPT4gY3NzYFxuICBiYWNrZ3JvdW5kLWltYWdlOiB1cmwoJHtpY29uVXJsfSk7XG4gIGJhY2tncm91bmQtcG9zaXRpb246IGNlbnRlcjtcbiAgYmFja2dyb3VuZC1yZXBlYXQ6IG5vLXJlcGVhdDtcbiAgYmFja2dyb3VuZC1zaXplOiBjb250YWluO1xuICBkaXNwbGF5OiBibG9jaztcbiAgaGVpZ2h0OiAyMnB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IGljb25XcmFwcGVyID0gY3NzYFxuICBhbGlnbi1pdGVtczogY2VudGVyO1xuICBkaXNwbGF5OiBmbGV4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3QgaW1pdGF0aW9uSWNvbkJ1dHRvbiA9IGNzc2BcbiAgYmFja2dyb3VuZC1jb2xvcjogJHtDb2xvcnMubmV1dHJhbDEwMH07XG4gIGJvcmRlci1yYWRpdXM6IDUwJTtcbiAgYm9yZGVyOiAxcHggc29saWQgJHtDb2xvcnMubmV1dHJhbDYwMH07XG4gIGN1cnNvcjogcG9pbnRlcjtcbiAgaGVpZ2h0OiA0NHB4O1xuICB3aWR0aDogNDRweDtcbiAgJjpob3ZlciB7XG4gICAgYm94LXNoYWRvdzogMHB4IDVweCAxMHB4IDBweCByZ2IoMCAwIDAgLyAyMCUpO1xuICB9XG5gO1xuIl19 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
const shareIcon = iconUrl => /*#__PURE__*/css("background-image:url(", iconUrl, ");background-position:center;background-repeat:no-repeat;background-size:contain;display:block;height:22px;" + (process.env.NODE_ENV === "production" ? "" : ";label:shareIcon;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInJlYnJhbmRTdHlsZXMudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBV2lEIiwiZmlsZSI6InJlYnJhbmRTdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9yZWFjdCc7XG5pbXBvcnQgeyBDb2xvcnMgfSBmcm9tICdAemFwaWVyL2Rlc2lnbi1zeXN0ZW0nO1xuXG5leHBvcnQgY29uc3Qgc3Bpbm5lciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAgaGVpZ2h0OiA0MHB4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3Qgc2hhcmVJY29uID0gKGljb25Vcmw6IHN0cmluZykgPT4gY3NzYFxuICBiYWNrZ3JvdW5kLWltYWdlOiB1cmwoJHtpY29uVXJsfSk7XG4gIGJhY2tncm91bmQtcG9zaXRpb246IGNlbnRlcjtcbiAgYmFja2dyb3VuZC1yZXBlYXQ6IG5vLXJlcGVhdDtcbiAgYmFja2dyb3VuZC1zaXplOiBjb250YWluO1xuICBkaXNwbGF5OiBibG9jaztcbiAgaGVpZ2h0OiAyMnB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IGljb25XcmFwcGVyID0gY3NzYFxuICBhbGlnbi1pdGVtczogY2VudGVyO1xuICBkaXNwbGF5OiBmbGV4O1xuICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcbiAgd2lkdGg6IDQwcHg7XG5gO1xuXG5leHBvcnQgY29uc3QgaW1pdGF0aW9uSWNvbkJ1dHRvbiA9IGNzc2BcbiAgYmFja2dyb3VuZC1jb2xvcjogJHtDb2xvcnMubmV1dHJhbDEwMH07XG4gIGJvcmRlci1yYWRpdXM6IDUwJTtcbiAgYm9yZGVyOiAxcHggc29saWQgJHtDb2xvcnMubmV1dHJhbDYwMH07XG4gIGN1cnNvcjogcG9pbnRlcjtcbiAgaGVpZ2h0OiA0NHB4O1xuICB3aWR0aDogNDRweDtcbiAgJjpob3ZlciB7XG4gICAgYm94LXNoYWRvdzogMHB4IDVweCAxMHB4IDBweCByZ2IoMCAwIDAgLyAyMCUpO1xuICB9XG5gO1xuIl19 */");
process.env.NODE_ENV === "production" ? {
  name: "r6mxso",
  styles: "align-items:center;display:flex;justify-content:center;width:40px"
} : {
  name: "ky6ky4-iconWrapper",
  styles: "align-items:center;display:flex;justify-content:center;width:40px;label:iconWrapper;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInJlYnJhbmRTdHlsZXMudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBb0I4QiIsImZpbGUiOiJyZWJyYW5kU3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vcmVhY3QnO1xuaW1wb3J0IHsgQ29sb3JzIH0gZnJvbSAnQHphcGllci9kZXNpZ24tc3lzdGVtJztcblxuZXhwb3J0IGNvbnN0IHNwaW5uZXIgPSBjc3NgXG4gIGFsaWduLWl0ZW1zOiBjZW50ZXI7XG4gIGRpc3BsYXk6IGZsZXg7XG4gIGhlaWdodDogNDBweDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IHNoYXJlSWNvbiA9IChpY29uVXJsOiBzdHJpbmcpID0+IGNzc2BcbiAgYmFja2dyb3VuZC1pbWFnZTogdXJsKCR7aWNvblVybH0pO1xuICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XG4gIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XG4gIGJhY2tncm91bmQtc2l6ZTogY29udGFpbjtcbiAgZGlzcGxheTogYmxvY2s7XG4gIGhlaWdodDogMjJweDtcbmA7XG5cbmV4cG9ydCBjb25zdCBpY29uV3JhcHBlciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IGltaXRhdGlvbkljb25CdXR0b24gPSBjc3NgXG4gIGJhY2tncm91bmQtY29sb3I6ICR7Q29sb3JzLm5ldXRyYWwxMDB9O1xuICBib3JkZXItcmFkaXVzOiA1MCU7XG4gIGJvcmRlcjogMXB4IHNvbGlkICR7Q29sb3JzLm5ldXRyYWw2MDB9O1xuICBjdXJzb3I6IHBvaW50ZXI7XG4gIGhlaWdodDogNDRweDtcbiAgd2lkdGg6IDQ0cHg7XG4gICY6aG92ZXIge1xuICAgIGJveC1zaGFkb3c6IDBweCA1cHggMTBweCAwcHggcmdiKDAgMCAwIC8gMjAlKTtcbiAgfVxuYDtcbiJdfQ== */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
const imitationIconButton = /*#__PURE__*/css("background-color:", Colors.neutral100, ";border-radius:50%;border:1px solid ", Colors.neutral600, ";cursor:pointer;height:44px;width:44px;&:hover{box-shadow:0px 5px 10px 0px rgb(0 0 0 / 20%);}" + (process.env.NODE_ENV === "production" ? "" : ";label:imitationIconButton;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInJlYnJhbmRTdHlsZXMudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBMkJzQyIsImZpbGUiOiJyZWJyYW5kU3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vcmVhY3QnO1xuaW1wb3J0IHsgQ29sb3JzIH0gZnJvbSAnQHphcGllci9kZXNpZ24tc3lzdGVtJztcblxuZXhwb3J0IGNvbnN0IHNwaW5uZXIgPSBjc3NgXG4gIGFsaWduLWl0ZW1zOiBjZW50ZXI7XG4gIGRpc3BsYXk6IGZsZXg7XG4gIGhlaWdodDogNDBweDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IHNoYXJlSWNvbiA9IChpY29uVXJsOiBzdHJpbmcpID0+IGNzc2BcbiAgYmFja2dyb3VuZC1pbWFnZTogdXJsKCR7aWNvblVybH0pO1xuICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XG4gIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XG4gIGJhY2tncm91bmQtc2l6ZTogY29udGFpbjtcbiAgZGlzcGxheTogYmxvY2s7XG4gIGhlaWdodDogMjJweDtcbmA7XG5cbmV4cG9ydCBjb25zdCBpY29uV3JhcHBlciA9IGNzc2BcbiAgYWxpZ24taXRlbXM6IGNlbnRlcjtcbiAgZGlzcGxheTogZmxleDtcbiAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XG4gIHdpZHRoOiA0MHB4O1xuYDtcblxuZXhwb3J0IGNvbnN0IGltaXRhdGlvbkljb25CdXR0b24gPSBjc3NgXG4gIGJhY2tncm91bmQtY29sb3I6ICR7Q29sb3JzLm5ldXRyYWwxMDB9O1xuICBib3JkZXItcmFkaXVzOiA1MCU7XG4gIGJvcmRlcjogMXB4IHNvbGlkICR7Q29sb3JzLm5ldXRyYWw2MDB9O1xuICBjdXJzb3I6IHBvaW50ZXI7XG4gIGhlaWdodDogNDRweDtcbiAgd2lkdGg6IDQ0cHg7XG4gICY6aG92ZXIge1xuICAgIGJveC1zaGFkb3c6IDBweCA1cHggMTBweCAwcHggcmdiKDAgMCAwIC8gMjAlKTtcbiAgfVxuYDtcbiJdfQ== */");

const icons = {
  Email: 'actionEmail',
  Embed: 'arrowCode',
  Slack: 'https://zapier-images.imgix.net/storage/services/6cf3f5a461feadfba7abc93c4c395b33_2.128x128.png?auto=format%2Ccompress&fit=crop&h=44&ixlib=python-3.0.0&q=50&w=44',
  Facebook: 'https://zapier-images.imgix.net/storage/services/f407c31b217aac6e0cd4171092d53a8c_3.png?auto=format%2Ccompress&fit=crop&h=44&ixlib=python-3.0.0&q=50&w=44',
  Twitter: 'https://zapier-images.imgix.net/storage/services/580b3c79622bbff634f0d764e9f813a3.png?auto=format%2Ccompress&fit=crop&h=44&ixlib=python-3.0.0&q=50&w=44',
  LinkedIn: 'https://zapier-images.imgix.net/storage/developer/82e76b99c17c252653cee811b4e46123_2.png?auto=format%2Ccompress&fit=crop&h=44&ixlib=python-3.0.0&q=50&w=44'
};

const isImageUrl = str => /^https/.test(str);

const RebrandContent = ({
  channel,
  isDisabled,
  handleClick,
  isLoading
}) => {
  if (isLoading) {
    return jsx("span", {
      css: spinner
    }, jsx(Spinner, null));
  }

  const IconWithTooltip = childProps => isImageUrl(icons[channel]) ? jsx("button", {
    css: imitationIconButton,
    disabled: isDisabled,
    onClick: handleClick
  }, jsx("span", {
    css: shareIcon(icons[channel])
  })) : jsx(IconButton, _extends({
    color: "icon-primary",
    disabled: isDisabled,
    icon: icons[channel],
    isCircle: true,
    onClick: handleClick,
    size: "small"
  }, childProps));

  return jsx(TooltipWrapper, {
    content: capitalize(channel),
    position: "north",
    showTooltipOnTouchDevices: false
  }, ({
    childProps
  }) => jsx(IconWithTooltip, childProps));
};

const OriginalContent = ({
  channel,
  isDisabled,
  handleClick,
  isLoading
}) => {
  return jsx(TooltipWrapper, {
    content: capitalize(channel),
    position: "north",
    showTooltipOnTouchDevices: false
  }, ({
    childProps
  }) => jsx(Button, _extends({}, childProps, {
    ariaLabel: `share via ${channel}`,
    color: "secondary",
    disabled: isDisabled,
    isSquare: true,
    onClick: handleClick,
    size: "small"
  }), isLoading ? jsx("span", {
    css: spinner$1
  }, jsx(Spinner, null)) : isImageUrl(icons[channel]) ? jsx("span", {
    css: shareIcon$1(icons[channel])
  }) : jsx("span", {
    css: iconWrapper
  }, jsx(Icon, {
    color: "gray60",
    name: icons[channel],
    size: 24
  }))));
};

const SharingButton = ({
  channel,
  isDisabled = false,
  isLoading = false,
  onClick
}) => {
  const {
    isRebrand
  } = useTheme();
  const track = useNamedTracking();
  const handleClick = useCallback(event => {
    event.preventDefault();
    track.zapSharingModal.socialChannelClicked(channel);
    onClick(channel);
  }, [channel, onClick, track.zapSharingModal]);
  return isRebrand ? jsx(RebrandContent, {
    channel: channel,
    handleClick: handleClick,
    isDisabled: isDisabled,
    isLoading: isLoading
  }) : jsx(OriginalContent, {
    channel: channel,
    handleClick: handleClick,
    isDisabled: isDisabled,
    isLoading: isLoading
  });
};

export { NamedTrackingProviderContainer as N, SharingButton as S, _extends as _, useIsRebrand as a, adjustUrlParams as b, createSocialMediaWindow as c, addParamsToUrl as d, extractParams as e, isEmptyString as i, useNamedTracking as u };
